import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BaseService } from './base.service';
import { ConfigState } from 'src/store/reducers/config.reducer';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';

export interface DiagramFilterConfiguration {
    id: number;
    name: string;
    componentIds?: number[];
}

@Injectable({
    providedIn: 'root',
})
export class DiagramFilterConfigService extends BaseService {
    protected baseUrl = '/api/diagramfilterconfiguration';
    protected http = inject(HttpClient);
    protected store = inject<Store<{ config: ConfigState }>>(Store);

    public async apply(configId: number): Promise<void> {
        const [sop, workspace] = await Promise.all([this.getSop(), this.getWorkspaceKey()]);
        const params = new HttpParams()
            .append('sop', sop)
            .append('workspace', workspace)
            .append('configurationId', configId);

        return firstValueFrom(
            this.http.post<void>(`${this.baseUrl}/apply-diagram-filter-configuration`, null, {
                headers: await this.createHeaders(),
                params,
            })
        );
    }

    public async clear(): Promise<void> {
        const [sop, workspace] = await Promise.all([this.getSop(), this.getWorkspaceKey()]);
        const params = new HttpParams().append('sop', sop).append('workspace', workspace);

        return firstValueFrom(
            this.http.delete<void>(`${this.baseUrl}/clear-applied-diagram-filter-configuration`, {
                headers: await this.createHeaders(),
                params,
            })
        );
    }

    public async create(configName: string, componentKeys: number[]): Promise<number> {
        const [sop, workspace] = await Promise.all([this.getSop(), this.getWorkspaceKey()]);
        const params = new HttpParams().append('sop', sop).append('workspace', workspace);

        return firstValueFrom(
            this.http.post<number>(
                `${this.baseUrl}/create-diagram-filter-configuration`,
                { Name: configName, ComponentIds: componentKeys },
                { headers: await this.createHeaders(), params }
            )
        );
    }

    public async delete(configId: number): Promise<void> {
        const [sop, workspace] = await Promise.all([this.getSop(), this.getWorkspaceKey()]);
        const params = new HttpParams()
            .append('sop', sop)
            .append('workspace', workspace)
            .append('id', configId);

        return firstValueFrom(
            this.http.delete<void>(`${this.baseUrl}/delete-diagram-filter-configuration`, {
                headers: await this.createHeaders(),
                params,
            })
        );
    }

    public async getAllFilterConfigs(): Promise<{
        configurations: DiagramFilterConfiguration[];
        appliedConfigurationId: number | null;
    }> {
        const workspace = await this.getWorkspaceKey();
        const params = new HttpParams().append('workspace', workspace);

        return firstValueFrom(
            this.http.get<{
                configurations: DiagramFilterConfiguration[];
                appliedConfigurationId: number | null;
            }>(`${this.baseUrl}/get-all-diagram-filter-configurations`, {
                headers: await this.createHeaders(),
                params,
            })
        );
    }
}
